/**
 * Body for PUT job/update-job/<id>/ when only the status changes (close,
 * reopen). JOB-03: closing carries the recruiter's email choice; reopening
 * (JOB-01/04) never asks to notify anyone.
 */

/** "Closed" status id (utils/constants jobStatus). */
export const JOB_STATUS_CLOSED_ID = 3;

export interface CloseJobOptions {
  notify_candidates?: boolean;
  email_template?: string | null;
  // P0-4: internal rejection reason for everyone the close rejects.
  rejection_reason?: string | null;
  rejection_note?: string | null;
}

export interface JobStatusPayload {
  status: string;
  interview_duration?: unknown;
  notify_candidates?: boolean;
  email_template?: string | null;
  rejection_reason?: string | null;
  rejection_note?: string | null;
}

export function buildJobStatusPayload(
  statusId: number | string,
  jobDetails?: { interview_duration?: unknown } | null,
  options?: CloseJobOptions | null,
): JobStatusPayload {
  const payload: JobStatusPayload = { status: String(statusId) };
  if (
    jobDetails?.interview_duration !== undefined &&
    jobDetails?.interview_duration !== null
  ) {
    payload.interview_duration = jobDetails.interview_duration;
  }
  // Only a close sends the email choice; other status changes stay as before.
  if (options && Number(statusId) === JOB_STATUS_CLOSED_ID) {
    const notify = Boolean(options.notify_candidates);
    payload.notify_candidates = notify;
    payload.email_template = notify ? options.email_template || null : null;
    if (options.rejection_reason) {
      payload.rejection_reason = options.rejection_reason;
      payload.rejection_note = options.rejection_note || null;
    }
  }
  return payload;
}

/** Close-modal choice → the options closeJob forwards. */
export function buildCloseJobOptions(
  sendEmail: boolean,
  template?: string | null,
  rejection?: { reason?: string | null; note?: string | null } | null,
): CloseJobOptions {
  const options: CloseJobOptions = {
    notify_candidates: Boolean(sendEmail),
    email_template: sendEmail ? template || null : null,
  };
  if (rejection?.reason) {
    options.rejection_reason = rejection.reason;
    options.rejection_note = rejection.note || null;
  }
  return options;
}

const toCount = (v: unknown): number => {
  const x = Number(v);
  return Number.isFinite(x) && x > 0 ? Math.floor(x) : 0;
};

/**
 * Counts shown in the close popup, from the job's dashboard counts
 * (`total_job_applications`, `hired`, `rejected`). "Not hired" = everyone who
 * is neither hired nor already rejected — the people a rejection email reaches.
 */
export function getCloseJobCounts(
  count?: {
    total_job_applications?: unknown;
    hired?: unknown;
    rejected?: unknown;
  } | null,
): { total: number; hired: number; notHired: number } {
  const total = toCount(count?.total_job_applications);
  const hired = toCount(count?.hired);
  const rejected = toCount(count?.rejected);
  return { total, hired, notHired: Math.max(0, total - hired - rejected) };
}
